/** A run of the body text: bold where it was written `**like this**`, italic where `*like this*`. */
export interface TextPart {
  text: string;
  bold: boolean;
  italic: boolean;
}

/**
 * The body text in blocks: lines as written, the lines that start with a bullet (`- `, `* ` or
 * `• `) or a number (`1. `, `2) `) as a list, and a line of three dashes or more (`---`) as a divider.
 */
export type TextBlock =
  | { kind: 'lines'; parts: TextPart[] }
  | { kind: 'bullets'; items: TextPart[][] }
  | { kind: 'numbers'; items: TextPart[][] }
  | { kind: 'divider' };

/** What a line of the body text starts with: a bullet or a list number, and how many characters. */
export interface LineMarker {
  kind: 'bullets' | 'numbers';
  length: number;
  /** A `1.` — a numbered list starts here, even right after another one. */
  first: boolean;
}

const DIVIDER = /^\s*-{3,}\s*$/;
const BULLET = /^\s*[-*•]\s+/;
const NUMBER = /^\s*(\d{1,3})([.)])\s+/;
/** A Danish month after `1.`: `1. maj` is a date, not a list. Short forms only with their dot (`jan.`), so a name like `Jan` is not one. */
const MONTH = /^(januar|februar|marts|april|maj|juni|juli|august|september|oktober|november|december|(jan|feb|mar|apr|jun|jul|aug|sep|sept|okt|nov|dec)\.)(?![\p{L}\p{N}])/iu;

/**
 * Each line's marker, or null. A numbered list starts at 1 and counts on by one, so a Danish
 * ordinal or date at the start of a line (`3. sal`, `24. december`) stays text; a month after a
 * `1.` keeps even `1. maj` text (`1)` starts a list whatever follows). Empty lines between the
 * items do not end a list. The admin app's list buttons (`utils/text-markup.ts`) mirror this.
 */
export function lineMarkers(lines: string[]): (LineMarker | null)[] {
  let next = 0; // the number that goes on the open list; 0 = none open
  return lines.map((line) => {
    if (!line.trim()) return null;
    const number = NUMBER.exec(line);
    const n = number ? +number[1] : 0;
    const date = number?.[2] === '.' && MONTH.test(line.slice(number[0].length));
    if (number && ((n === 1 && !date) || (n > 1 && n === next))) {
      next = n + 1;
      return { kind: 'numbers', length: number[0].length, first: n === 1 };
    }
    next = 0;
    if (DIVIDER.test(line)) return null;
    const bullet = BULLET.exec(line);
    return bullet ? { kind: 'bullets', length: bullet[0].length, first: false } : null;
  });
}

/** The text in blocks: runs of plain lines (line breaks kept), lists, and dividers. */
export function textBlocks(text: string | null): TextBlock[] {
  if (!text) return [];
  const blocks: TextBlock[] = [];
  let lines: string[] = [];
  const flushLines = () => {
    if (lines.length) blocks.push({ kind: 'lines', parts: textParts(lines.join('\n')) });
    lines = [];
  };
  const all = text.split('\n');
  const markers = lineMarkers(all);
  all.forEach((line, i) => {
    if (DIVIDER.test(line)) {
      flushLines();
      blocks.push({ kind: 'divider' });
      return;
    }
    const marker = markers[i];
    if (!marker) {
      lines.push(line);
      return;
    }
    const last = blocks.at(-1);
    // On the list above, when only empty lines came since its last item: they belong to the list.
    const goesOn = last?.kind === marker.kind && !marker.first && lines.every((l) => !l.trim());
    if (goesOn) lines = [];
    flushLines();
    const item = textParts(line.slice(marker.length));
    if (goesOn) last.items.push(item);
    else blocks.push({ kind: marker.kind, items: [item] });
  });
  flushLines();
  return blocks;
}

/**
 * Bold and italic between `***`, bold between pairs of `**`, italic between single `*` that hug
 * their text (`*kursiv*`, not `5 * 3`). A marker without its pair is kept as written, so a stray
 * one shows rather than changing the rest of the text.
 */
const MARKED = /\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*([^*\s](?:[^*]*?[^*\s])?)\*/gs;

/** The text in runs, bold or italic where it was marked. */
export function textParts(text: string | null): TextPart[] {
  if (!text) return [];
  const parts: TextPart[] = [];
  let from = 0;
  for (const match of text.matchAll(MARKED)) {
    if (match.index > from) parts.push({ text: text.slice(from, match.index), bold: false, italic: false });
    const [, both, bold, italic] = match;
    parts.push({ text: both ?? bold ?? italic, bold: both !== undefined || bold !== undefined, italic: bold === undefined });
    from = match.index + match[0].length;
  }
  if (from < text.length) parts.push({ text: text.slice(from), bold: false, italic: false });
  return parts;
}
